import React = require('react');
import { Formik, FormikProps } from 'formik';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { Messages } from '../../codegen/Localize';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import _ = require('underscore');

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';

import { FormikCheckbox } from '../../commonInfra/components/FormikCheckbox';
import { NotificationsSettingsProps } from './NotificationsSettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

const DEEPLINK_ID = 'subscriptions';

export interface SubscriptionNotificationField {
    subscriptionNotificationEnabled: boolean;
}

function showButtons(formik: FormikProps<SubscriptionNotificationField>, subscriptionNotificationEnabled: boolean) {
    if (!formik.touched || !formik.isValid || formik.isSubmitting || _.isEqual(formik.values.subscriptionNotificationEnabled, subscriptionNotificationEnabled)) {
        return false;
    }
    return true;
}

export function SubscriptionNotification(props: NotificationsSettingsProps): JSX.Element {
    const [currentSubscriptionNotificationEnabled, setCurrentSubscriptionNotificationEnabled] = React.useState(false);

    async function handleSubmit(values: SubscriptionNotificationField) {
        UserSettings.updateSuspendedSubscriptionSetting(props.siteUser.id, values.subscriptionNotificationEnabled).then(() => {
            setCurrentSubscriptionNotificationEnabled(values.subscriptionNotificationEnabled);
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });
        }).catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
        });
    }

    React.useEffect(
        () => {
            UserSettings.getSuspendedSubscriptionSetting(props.serverUser.id)
                .then((result: boolean) => {
                    setCurrentSubscriptionNotificationEnabled(result);
                }).catch(() => { });
        },
        // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <Formik
            initialValues={{ subscriptionNotificationEnabled: currentSubscriptionNotificationEnabled }}
            onSubmit={(values: SubscriptionNotificationField) => handleSubmit(values)}
            enableReinitialize={true}
        >
            {
                (formik: FormikProps<SubscriptionNotificationField>) => (
                    <DeepLink deepLinkId={DEEPLINK_ID}>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ display: 'flex' }}>
                              <div data-tb-test-id="userSettings-subscription-notification">
                                  <div className={UserSettingsStyle.settingsLabelText}>
                                      <h4>{Messages.subscriptions()}</h4>
                                  </div>
                              </div>
                              <div className={UserSettingsStyle.settingsLabelDescription}>
                                <div className={UserSettingsStyle.detailsValue}>
                                    {Messages.subscriptionSuspendedNotificationUserSettingDetail()}
                                </div>
                                <div className={UserSettingsStyle.detailsValue}>
                                  <FormikCheckbox
                                      containerStyle={{ marginLeft: 0 }}
                                      testId='Subscription-Notification-Enabled'
                                      name={'subscriptionNotificationEnabled'}
                                      disabled={props.userHasNoSiteId}
                                      label={Messages.subscriptionSuspendedNotificationUserSettingEnable()} />
                                </div>
                                {
                                    showButtons(formik, currentSubscriptionNotificationEnabled) &&
                                    <InlineCancelAndSaveButtons
                                        testId={'subscription-notification'}
                                        formik={formik}
                                    />
                                }
                              </div>
                            </div>
                        </form>
                    </DeepLink>
                )
            }
        </Formik>
    );
}

export const SubscriptionsSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.subscriptions(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  tags:  () => { return [Messages.subscriptionSuspendedNotificationUserSettingDetail(), Messages.userNotificationSettingsLabel()]; },
  isAdmin: true // props.isViewerAdmin
});